import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { JsonMapper } from "@nstep-common/utils";
import { JwtTokenModel } from "projects/common/core/models/jwt-token.model";

const TOKEN_OFFSET_SECONDS = 10;

/*
 This helper is recommended to be used instead of directly using the local storage (with exceptions) because:
 - it manages and abstracts the local storage
 - prevents circular service dependencies for simple session checks
 - allows backing and restoring the session while impersonating without leaving left-overs
*/
@Injectable({
	providedIn: 'root'
})
export class SessionService {
	private jwt: JwtTokenModel | null = null;
	private cachedAccessToken: string | null = null;

	constructor(private jwtHelper: JwtHelperService) {
	}

	backup(): void {
		this.invalidateJwtCache();

		const backup = JSON.stringify(
			Object.fromEntries(
				Object.keys(localStorage).map((k) => [k, localStorage.getItem(k)!])
			)
		);

		localStorage.clear();
		localStorage.setItem('_backup', backup);
	}

	restore(): void {
		this.invalidateJwtCache();

		const json = localStorage.getItem('_backup');
		if (!json) {
			return;
		}

		let data: Record<string, string>;
		try {
			data = JSON.parse(json) as Record<string, string>;
		}
		catch {
			this.clear();
			return;
		}

		if (!data || typeof data !== 'object' || Array.isArray(data)) {
			this.clear();
			return;
		}

		localStorage.clear();
		for (const [key, value] of Object.entries(data)) {
			localStorage.setItem(key, value);
		}
	}

	clear(): void {
		localStorage.clear();
		this.invalidateJwtCache();
	}

	get(key: string): string | null {
		return localStorage.getItem(key);
	}

	set(key: string, value: string): void {
		localStorage.setItem(key, value);
		if (key === 'accessToken') {
			this.invalidateJwtCache();
		}
	}

	remove(key: string): void {
		localStorage.removeItem(key);
		if (key === 'accessToken') {
			this.invalidateJwtCache();
		}
	}

	getAccessToken(): string {
		return this.get('accessToken') ?? '';
	}

	setAccessToken(accessToken: string): void {
		this.set('accessToken', accessToken);
	}

	getJwt(checkLifetime = false): JwtTokenModel | null {
		const accessToken = this.getAccessToken();

		if (this.jwt != null) {
			let tokenExpired = false;
			try {
				tokenExpired = checkLifetime && this.jwtHelper.isTokenExpired(accessToken, TOKEN_OFFSET_SECONDS);
			}
			catch {
				tokenExpired = true;
			}

			if (this.cachedAccessToken !== accessToken || tokenExpired) {
				this.invalidateJwtCache();
				return null;
			}

			return this.jwt;
		}

		if (!accessToken) {
			return null;
		}

		try {
			if (checkLifetime && this.jwtHelper.isTokenExpired(accessToken, TOKEN_OFFSET_SECONDS)) {
				return null;
			}
		}
		catch {
			return null;
		}

		let decodedToken: any;
		try {
			decodedToken = this.jwtHelper.decodeToken(accessToken);
		}
		catch {
			return null;
		}

		if (!decodedToken || typeof decodedToken !== 'object') {
			return null;
		}

		// Fallback for claims whose shape can vary between tokens.
		if (!decodedToken.Access) {
			decodedToken.Access = [];
		}
		else if (typeof decodedToken.Access === 'string') {
			decodedToken.Access = [decodedToken.Access];
		}

		// Fallback for claims whose shape can vary between tokens.
		if (!decodedToken.SubRole) {
			decodedToken.SubRole = [];
		}
		else if (typeof decodedToken.SubRole === 'string') {
			decodedToken.SubRole = [decodedToken.SubRole];
		}

		if (!decodedToken.ProfileId) {
			decodedToken.ProfileId = null;
		}

		if (decodedToken.RoleId == null || decodedToken.RoleId === '') {
			decodedToken.RoleId = null;
		}
		else {
			const roleId = Number(decodedToken.RoleId);
			decodedToken.RoleId = Number.isNaN(roleId) ? null : roleId;
		}

		if (!decodedToken.ImpersonatedBy) {
			decodedToken.ImpersonatedBy = null;
		}

		if (!decodedToken.HasTotalAccount) {
			decodedToken.HasTotalAccount = null;
		}

		const jsonMapper = new JsonMapper();
		const mapResult = jsonMapper.deserializeObject(decodedToken, JwtTokenModel);

		if (Object.keys(mapResult.errors).length) {
			return null;
		}

		this.jwt = mapResult.value;
		this.cachedAccessToken = accessToken;
		return this.jwt;
	}

	private invalidateJwtCache(): void {
		this.jwt = null;
		this.cachedAccessToken = null;
	}

	hasAccessExpired(): boolean {
		return this.getJwt(true) == null;
	}
}